import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { isoToDDMMYYYY as formatDate } from '../utils/dateFormat.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const REALTIME_DEBOUNCE = 1500

// Schedule labels match Tenant.PAYMENT_SCHEDULES values from the API.
const SCHEDULE_LABELS = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ENGLISH_QUARTER_DAYS: 'English Quarter Days',
  SCOTTISH_QUARTER_DAYS: 'Scottish Quarter Days',
  ANNUALLY: 'Annually'
}

const EVENT_TYPES = ['Current Lease', 'Current Rent Free', 'Void', 'Rent Free', 'Paying Rent']

// Display columns split into 3 groups always visible + 2 technical groups
// (Start Period / End Period) hidden behind the toggle. Order matters: it
// matches the data row layout below.
const BASE_COLUMNS = [
  // Tenant is rendered in the fixed-columns pane (fixedColumnsStart={1}).
  // Keep it in its own group so the "General" merge does not cross the
  // freeze boundary — Handsontable splits cross-boundary merges into two
  // independent cells, which breaks header height and cascades row offsets.
  { key: 'tenant_label',         group: 'tenant',              label: 'Tenant' },
  { key: 'event_type',           group: 'general',             label: 'Event Type' },
  { key: 'payment_schedule',     group: 'general',             label: 'Payment Frequency' },
  { key: 'mla_name',             group: 'general',             label: 'MLA Name' },
  { key: 'mla_cycle',            group: 'general',             label: 'MLA Instance' },
  { key: 'start_date',           group: 'event_dates',         label: 'Start Date' },
  { key: 'end_date',             group: 'event_dates',         label: 'End Date' },
  { key: 'cash_flow_role_label', group: 'cash_flow_treatment', label: 'Cash Flow Role' },
  { key: 'rent_basis_label',     group: 'cash_flow_treatment', label: 'Rent Basis' },
  { key: 'rent_lock_date',       group: 'cash_flow_treatment', label: 'Locked Rent Date' },
  { key: 'included_in_label',    group: 'cash_flow_treatment', label: 'Included In' }
]

const TECHNICAL_COLUMNS = [
  { key: 'start_period_start',   group: 'start_period', label: 'Start Period Start' },
  { key: 'start_period_end',     group: 'start_period', label: 'Start Period End' },
  { key: 'start_period_days',    group: 'start_period', label: 'Days in Period' },
  { key: 'start_period_overlap', group: 'start_period', label: 'Overlap' },
  { key: 'start_date_pro_rata',  group: 'start_period', label: 'Start Pro-Rata' },
  { key: 'end_period_start',     group: 'end_period',   label: 'End Period Start' },
  { key: 'end_period_end',       group: 'end_period',   label: 'End Period End' },
  { key: 'end_period_days',      group: 'end_period',   label: 'Days in Period' },
  { key: 'end_period_overlap',   group: 'end_period',   label: 'Overlap' },
  { key: 'end_date_pro_rata',    group: 'end_period',   label: 'End Pro-Rata' }
]

const GROUP_LABELS = {
  tenant: '',
  general: 'General',
  event_dates: 'Event Dates',
  cash_flow_treatment: 'Cash Flow Treatment',
  start_period: 'Start Period',
  end_period: 'End Period'
}

function formatProRata(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })
}

function formatCell(column, row) {
  const raw = row[column.key]
  if (raw === null || raw === undefined) return ''
  switch (column.key) {
    case 'payment_schedule':
      return SCHEDULE_LABELS[raw] || raw
    case 'start_date':
    case 'end_date':
    case 'rent_lock_date':
    case 'start_period_start':
    case 'start_period_end':
    case 'end_period_start':
    case 'end_period_end':
      return formatDate(raw)
    case 'start_date_pro_rata':
    case 'end_date_pro_rata':
      return formatProRata(raw)
    default:
      return String(raw)
  }
}

function buildGroupHeaderRow(columns) {
  const cells = []
  let mergeRanges = []
  let i = 0
  while (i < columns.length) {
    let j = i
    while (j < columns.length && columns[j].group === columns[i].group) j += 1
    cells.push(GROUP_LABELS[columns[i].group])
    for (let k = i + 1; k < j; k += 1) cells.push('')
    if (j - i > 1) mergeRanges.push({ row: 0, col: i, rowspan: 1, colspan: j - i })
    i = j
  }
  return { row: cells, merges: mergeRanges }
}

export default function LeasingEventsTable({ valuationId, dataUrl }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rows, setRows] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [showTechnical, setShowTechnical] = useState(false)
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [tenantFilter, setTenantFilter] = useState('all')
  const fetchRef = useRef(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetch(dataUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) {
        const code = json.error || `Request failed (${res.status})`
        throw new Error(code === 'access_denied' ? "You don't have access to this valuation." : code)
      }
      setRows((json.data || {}).rows || [])
    } catch (err) {
      setError(err.message || 'Failed to load')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dataUrl])

  useEffect(() => { fetchRef.current = fetchData }, [fetchData])
  useEffect(() => { fetchData() }, [fetchData])

  // Subscribe to ValuationChannel for live recalc when Tenant / MLA / Growth
  // changes — the channel is shared with Cash Flow / Market Rent Growth tabs.
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => { fetchRef.current?.(true) }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const columns = useMemo(
    () => (showTechnical ? [...BASE_COLUMNS, ...TECHNICAL_COLUMNS] : BASE_COLUMNS),
    [showTechnical]
  )

  const tenantOptions = useMemo(() => {
    const seen = new Map()
    rows.forEach(r => { if (!seen.has(r.tenant_id)) seen.set(r.tenant_id, r.tenant_label) })
    return Array.from(seen, ([id, label]) => ({ id, label }))
  }, [rows])

  const filteredRows = useMemo(() => rows.filter(r => {
    if (eventTypeFilter !== 'all' && r.event_type !== eventTypeFilter) return false
    if (tenantFilter !== 'all' && String(r.tenant_id) !== String(tenantFilter)) return false
    return true
  }), [rows, eventTypeFilter, tenantFilter])

  const groupHeader = useMemo(() => buildGroupHeaderRow(columns), [columns])
  const columnHeaderRow = useMemo(() => columns.map(c => c.label), [columns])

  const tableData = useMemo(() => {
    const dataRows = filteredRows.map(row => columns.map(col => formatCell(col, row)))
    return [groupHeader.row, columnHeaderRow, ...dataRows]
  }, [filteredRows, columns, groupHeader, columnHeaderRow])

  const fixedColumnsStart = 1 // freeze Tenant column

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )
  }

  if (error) return <div className="alert alert-danger m-3">{error}</div>

  return (
    <div className="leasing-events-container">
      <div className="d-flex flex-wrap gap-2 px-3 py-2 align-items-center">
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowTechnical(!showTechnical)}>
          {showTechnical ? 'Hide Technical Columns' : 'Show Technical Columns'}
        </button>

        {showFilters && (
          <>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">Event:</label>
              <select className="form-select form-select-sm" style={{ width: 180 }}
                      value={eventTypeFilter}
                      onChange={(e) => setEventTypeFilter(e.target.value)}>
                <option value="all">All</option>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">Tenant:</label>
              <select className="form-select form-select-sm" style={{ width: 220 }}
                      value={tenantFilter}
                      onChange={(e) => setTenantFilter(e.target.value)}>
                <option value="all">All</option>
                {tenantOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          </>
        )}

        <span className="ms-auto text-muted small">
          {filteredRows.length} of {rows.length} events
        </span>
      </div>

      {filteredRows.length === 0 ? (
        <div className="text-muted text-center py-5">
          No leasing events. Configure tenants and MLAs to populate this tab.
        </div>
      ) : (
        <div className="cash-flow-table-viewport">
          <HotTable
            data={tableData}
            rowHeaders={false}
            colHeaders={false}
            fixedColumnsStart={fixedColumnsStart}
            fixedRowsTop={2}
            themeName="ht-theme-horizon"
            licenseKey={HOT_LICENSE_KEY}
            sanitizer={sanitizeHandsontableHtml}
            stretchH="none"
            width="100%"
            height="auto"
            readOnly={true}
            manualColumnResize={true}
            manualRowResize={false}
            mergeCells={groupHeader.merges}
            cells={(row, col) => {
              if (row === 0) return { readOnly: true, className: 'htCenter htMiddle leasing-events-group-header' }
              if (row === 1) return { readOnly: true, className: 'htCenter htMiddle leasing-events-col-header' }
              const column = columns[col]
              const rightAligned = ['mla_cycle', 'start_period_days', 'start_period_overlap',
                                    'start_date_pro_rata', 'end_period_days', 'end_period_overlap',
                                    'end_date_pro_rata'].includes(column?.key)
              return { readOnly: true, className: rightAligned ? 'htRight' : 'htLeft' }
            }}
          />
        </div>
      )}
    </div>
  )
}
